export type TrackingConsentChoice = "granted" | "denied";

const TRACKING_CONSENT_STORAGE_KEY = "approbot_tracking_consent_v1";
const GOOGLE_TAG_SCRIPT_ID = "approbot-gtag-script";
const GOOGLE_TAG_CONFIGURED_IDS = new Set<string>();

const GA4_MEASUREMENT_ID =
  process.env.REACT_APP_GA4_MEASUREMENT_ID || process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "";
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";

const HOTJAR_SCRIPT_ID = "approbot-hotjar-script";
const HOTJAR_ID = process.env.REACT_APP_HOTJAR_ID || "6531718";
const HOTJAR_VERSION = process.env.REACT_APP_HOTJAR_VERSION || "6";

let trackingInitialized = false;
let hotjarInitialized = false;

function ensureGtagStub() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
}

function appendScriptOnce(id: string, src: string) {
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function configureGoogleTag(id: string, config?: Record<string, unknown>) {
  if (!id || GOOGLE_TAG_CONFIGURED_IDS.has(id) || typeof window === "undefined" || !window.gtag) return;
  window.gtag("config", id, config || {});
  GOOGLE_TAG_CONFIGURED_IDS.add(id);
}

function loadHotjar() {
  if (
    hotjarInitialized ||
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !HOTJAR_ID ||
    !HOTJAR_VERSION
  ) {
    return;
  }
  hotjarInitialized = true;
  window.hj =
    window.hj ||
    function hotjarStub(...args: unknown[]) {
      (window.hj!.q = window.hj!.q || []).push(args);
    };
  window._hjSettings = {
    hjid: Number(HOTJAR_ID),
    hjsv: Number(HOTJAR_VERSION),
  };
  appendScriptOnce(
    HOTJAR_SCRIPT_ID,
    `https://static.hotjar.com/c/hotjar-${window._hjSettings.hjid}.js?sv=${window._hjSettings.hjsv}`
  );
}

function consentValue(choice: TrackingConsentChoice) {
  return choice === "granted" ? "granted" : "denied";
}

export function initializeTracking() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  ensureGtagStub();

  if (!trackingInitialized) {
    window.gtag?.("consent", "default", {
      ad_storage: "denied",
      analytics_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });
    window.gtag?.("set", "url_passthrough", true);
    trackingInitialized = true;
  }

  const primaryTagId = GA4_MEASUREMENT_ID || GOOGLE_ADS_ID;
  if (primaryTagId) {
    appendScriptOnce(GOOGLE_TAG_SCRIPT_ID, `https://www.googletagmanager.com/gtag/js?id=${primaryTagId}`);
  }

  configureGoogleTag(GA4_MEASUREMENT_ID, { send_page_view: false });
  configureGoogleTag(GOOGLE_ADS_ID);

  const storedConsent = getStoredTrackingConsent();
  if (storedConsent) {
    applyTrackingConsent(storedConsent);
  }
}

export function getStoredTrackingConsent(): TrackingConsentChoice | null {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(TRACKING_CONSENT_STORAGE_KEY);
  if (value === "granted" || value === "denied") return value;
  return null;
}

export function applyTrackingConsent(choice: TrackingConsentChoice) {
  if (typeof window === "undefined" || !window.gtag) return;
  const mode = consentValue(choice);
  window.gtag("consent", "update", {
    ad_storage: mode,
    analytics_storage: mode,
    ad_user_data: mode,
    ad_personalization: mode,
  });
  if (choice === "granted") {
    loadHotjar();
  }
}

export function setTrackingConsent(choice: TrackingConsentChoice) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(TRACKING_CONSENT_STORAGE_KEY, choice);
  }
  applyTrackingConsent(choice);
}

export function hasAnyTrackingProviderConfigured() {
  return Boolean(GA4_MEASUREMENT_ID || GOOGLE_ADS_ID || HOTJAR_ID);
}

export function trackPageViewWithGoogleTag(pagePath: string) {
  if (typeof window === "undefined" || !window.gtag || !GA4_MEASUREMENT_ID) return;
  window.gtag("event", "page_view", { page_path: pagePath });
}
